import { graphql } from 'react-relay';
import { useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type { GqlRestrictionsInput } from '../RestrictionsGql/convertRestrictionsToGqlRestrictionsInput.ts';
import { convertRestrictionsToGqlRestrictionsInput } from '../RestrictionsGql/convertRestrictionsToGqlRestrictionsInput.ts';

import type {
	useFetchRestrictionsRESTObjRestrictionsRESTQuery as useFetchRestrictionsRESTObjRestrictionsRESTQuery$data,
	useFetchRestrictionsRESTObjRestrictionsRESTQueryVariables as useFetchRestrictionsRESTObjRestrictionsRESTQuery$variables,
} from './__types__/useFetchRestrictionsRESTObjRestrictionsRESTQuery.ts';
import { transformGraphQLQueryToRESTObj } from './restrictionsRESTUtils.ts';
import type { RestrictionsRESTObj } from './restrictionsRESTUtils.ts';
const useFetchRestrictionsRESTObjRestrictionsRESTQuery = graphql`
	query useFetchRestrictionsRESTObjRestrictionsRESTQuery($contentId: ID!, $draftShareId: String) {
		content(id: $contentId, status: ["current", "draft"], draftShareId: $draftShareId) {
			nodes {
				id
				type
				restrictions {
					read {
						operation
						restrictions {
							groupWithRestrictions {
								nodes {
									id
									name
									usageType
								}
							}
							userWithRestrictions {
								nodes {
									accountId
									displayName
									email
									hasSpaceEditPermission
									hasSpaceViewPermission
									permissionType
									publicName
									profilePicture {
										path
									}
									type
								}
							}
						}
					}
					update {
						operation
						restrictions {
							groupWithRestrictions {
								nodes {
									id
									name
									usageType
								}
							}
							userWithRestrictions {
								nodes {
									accountId
									displayName
									email
									hasSpaceEditPermission
									hasSpaceViewPermission
									permissionType
									publicName
									profilePicture {
										path
									}
									type
								}
							}
						}
					}
				}
			}
		}
	}
`;
export type UseFetchRestrictionsRESTObjParamsType = {
	contentId: string;
	onSuccess?: (restrictions: RestrictionsRESTObj) => void;
	/**
	 * Only called when 'use_graphql_to_publish_pages_and_blogs' experiment is enabled.
	 * Will replace onSuccess when experiment is cleaned up
	 */
	onSuccessGql?: (restrictions: GqlRestrictionsInput) => void;
};
export type UseFetchRestrictionsRESTObjType = {
	error: Error | ApolloErrorInterop | undefined;
	refetch: () => void;
};
export const useFetchRestrictionsRESTObj = ({
	contentId,
	onSuccess,
	onSuccessGql,
}: UseFetchRestrictionsRESTObjParamsType): UseFetchRestrictionsRESTObjType => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [transformationError, setTransformationError] = useState<Error | undefined>(undefined);
	const { error, refetch } = useQueryInterop<
		useFetchRestrictionsRESTObjRestrictionsRESTQuery$data,
		useFetchRestrictionsRESTObjRestrictionsRESTQuery$variables
	>(useFetchRestrictionsRESTObjRestrictionsRESTQuery, {
		variables: {
			contentId,
			draftShareId: getDraftShareIdFromQueryParam(),
		},
		// Without this setting, onCompleted will not fire on refetches
		// See: https://github.com/apollographql/react-apollo/issues/3709#issuecomment-592786578
		notifyOnNetworkStatusChange: true,
		fetchPolicy: 'network-only',
		onCompleted: (completedData) => {
			try {
				onSuccessGql?.(convertRestrictionsToGqlRestrictionsInput(completedData));
				onSuccess?.(transformGraphQLQueryToRESTObj(completedData));
				setTransformationError(undefined);
			} catch (restrictionsError) {
				setTransformationError(restrictionsError);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'error',
						actionSubject: 'fetchRestrictions',
						actionSubjectId: 'fetchRestrictionsError',
						source: 'useFetchRestrictionsRESTObj',
						attributes: {
							message: restrictionsError.message,
						},
					},
				}).fire();
			}
		},
	});

	return {
		error: error || transformationError,
		refetch,
	};
};
